import { useEffect } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import { motion, AnimatePresence } from 'motion/react'
import { LuArrowUpRight, LuSparkles, LuX } from 'react-icons/lu'

import logoWhite from '#/assets/logo-icon-yellow.png'
import { cn } from '#/lib/utils'

export type NavLinkItem = {
  label: string
  to: string
  badge?: string
}

type MobileNavOverlayProps = {
  isOpen: boolean
  onClose: () => void
  navLinks: NavLinkItem[]
}

const EASE_AWWWARDS = [0.22, 1, 0.36, 1] as const

// ─── Islamic Geometric Pattern Watermark ─────────────────────────────────────
function IslamicPatternOverlay() {
  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full min-h-screen w-full opacity-[0.035]"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <pattern
          id="mobile-nav-pattern"
          x="0"
          y="0"
          width="80"
          height="80"
          patternUnits="userSpaceOnUse"
        >
          <g fill="none" stroke="#ffb606" strokeWidth="0.8">
            <polygon points="40,3 44,28 65,17 51,35 76,40 51,45 65,63 44,52 40,77 36,52 15,63 29,45 4,40 29,35 15,17 36,28" />
            <circle cx="40" cy="40" r="12" stroke="#ffb606" strokeWidth="0.4" />
          </g>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#mobile-nav-pattern)" />
    </svg>
  )
}

export function MobileNavOverlay({
  isOpen,
  onClose,
  navLinks,
}: MobileNavOverlayProps) {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })

  // Full cross-platform & iOS body scroll lock
  useEffect(() => {
    if (!isOpen) return

    const scrollY = window.scrollY
    const originalOverflow = document.body.style.overflow
    const originalPosition = document.body.style.position
    const originalTop = document.body.style.top
    const originalWidth = document.body.style.width

    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.width = '100%'
    document.documentElement.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = originalOverflow
      document.body.style.position = originalPosition
      document.body.style.top = originalTop
      document.body.style.width = originalWidth
      document.documentElement.style.overflow = ''
      window.scrollTo(0, scrollY)
    }
  }, [isOpen])

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          key="mobile-nav-root"
          className="fixed inset-0 z-[100] flex w-full max-w-full flex-col justify-between overflow-hidden overflow-x-hidden bg-[#051c16] text-white overscroll-none select-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{
            opacity: 0,
            transition: { duration: 0.25, ease: EASE_AWWWARDS },
          }}
        >
          {/* Ambient Lighting & Pattern */}
          <IslamicPatternOverlay />
          <div
            aria-hidden
            className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-primary-500/15 blur-3xl"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-secondary-500/20 blur-3xl"
          />

          {/* ─── Top Header Strip ─────────────────────────────────────── */}
          <div className="relative z-10 flex items-center justify-between border-b border-white/10 px-6 py-5">
            <Link
              to="/"
              onClick={onClose}
              className="flex items-center gap-3 group cursor-pointer"
            >
              <div className="relative flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 border border-primary-500/30 p-1.5 transition-transform duration-300 group-hover:scale-105">
                <img
                  alt="Wa'allamah logo"
                  className="h-full w-full object-contain"
                  src={logoWhite}
                />
              </div>
              <div className="flex flex-col">
                <span className="text-base font-bold tracking-tight text-white group-hover:text-primary-300 transition-colors">
                  Wa'allamah
                </span>
                <span className="text-[11px] font-medium tracking-widest text-primary-400/90 uppercase">
                  Quran Institute
                </span>
              </div>
            </Link>

            {/* Circular Close Button with Micro-Animation */}
            <motion.button
              type="button"
              aria-label="Close menu"
              onClick={onClose}
              whileHover={{ scale: 1.08, rotate: 90 }}
              whileTap={{ scale: 0.94 }}
              transition={{ duration: 0.2 }}
              className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/90 backdrop-blur-md transition-colors hover:border-primary-500/60 hover:bg-primary-500/20 hover:text-primary-300"
            >
              <LuX className="h-5 w-5" />
            </motion.button>
          </div>

          {/* ─── Main Menu Navigation (Staggered Vertical Reveal) ──────── */}
          <div className="relative z-10 flex-1 overflow-y-auto overflow-x-hidden px-6 py-6 scrollbar-none flex flex-col justify-center overscroll-contain">
            <motion.nav
              className="flex flex-col gap-2"
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.05,
                    delayChildren: 0.06,
                  },
                },
              }}
            >
              {navLinks.map((link, idx) => {
                const isActive =
                  link.to === '/'
                    ? pathname === '/'
                    : pathname.startsWith(link.to)

                const indexFormatted = String(idx + 1).padStart(2, '0')

                return (
                  <motion.div
                    key={link.label}
                    variants={{
                      hidden: { opacity: 0, y: 16 },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: { duration: 0.45, ease: EASE_AWWWARDS },
                      },
                    }}
                  >
                    <Link
                      to={link.to}
                      onClick={onClose}
                      className={cn(
                        'group relative flex w-full cursor-pointer items-center justify-between rounded-2xl px-4 py-3 text-left transition-all duration-300',
                        isActive
                          ? 'bg-primary-500/15 border border-primary-500/30'
                          : 'hover:bg-white/5 border border-transparent hover:border-white/10',
                      )}
                    >
                      <div className="flex items-baseline gap-3.5">
                        <span className="font-mono text-xs font-semibold tracking-wider text-primary-400/70">
                          {indexFormatted}
                        </span>
                        <span
                          className={cn(
                            'text-2xl font-bold tracking-tight transition-colors duration-200',
                            isActive
                              ? 'text-primary-300'
                              : 'text-neutral-100 group-hover:text-primary-300',
                          )}
                        >
                          {link.label}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        {link.badge && (
                          <span className="rounded-full bg-primary-500/20 px-2.5 py-0.5 text-[10px] font-semibold text-primary-300 border border-primary-500/30 uppercase tracking-wider">
                            {link.badge}
                          </span>
                        )}
                        <div
                          className={cn(
                            'flex h-8 w-8 items-center justify-center rounded-full transition-all duration-300',
                            isActive
                              ? 'bg-primary-500 text-neutral-900 shadow-md shadow-primary-500/30'
                              : 'bg-white/5 text-neutral-400 group-hover:bg-primary-500/20 group-hover:text-primary-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5',
                          )}
                        >
                          <LuArrowUpRight className="h-4 w-4" />
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                )
              })}
            </motion.nav>
          </div>

          {/* ─── Bottom Action Strip ─────────────────────────────────── */}
          <motion.div
            className="relative z-10 border-t border-white/10 bg-[#03140f]/80 px-6 py-5 backdrop-blur-lg"
            initial={{ opacity: 0, y: 16 }}
            animate={{
              opacity: 1,
              y: 0,
              transition: { duration: 0.45, delay: 0.2, ease: EASE_AWWWARDS },
            }}
          >
            {/* Primary Action Button */}
            <Link
              to="/subscribe"
              onClick={onClose}
              className="relative flex h-13 w-full cursor-pointer items-center justify-center gap-2.5 overflow-hidden rounded-2xl bg-gradient-to-r from-primary-500 via-primary-400 to-primary-500 px-6 text-sm font-bold text-neutral-950 shadow-lg shadow-primary-500/25 transition-all duration-300 hover:shadow-primary-500/40 hover:scale-[1.01] active:scale-[0.99]"
            >
              <LuSparkles className="h-4 w-4 animate-pulse" />
              <span>Book Free Trial Class</span>
              <span className="rounded-full bg-neutral-950/15 px-2 py-0.5 text-[11px] font-semibold">
                100% Free
              </span>
            </Link>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
